<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Make Purchase</h5>
          <p class="page-sub">Record purchased goods from suppliers</p>
        </div>
        <button type="button" class="btn-outline" data-bs-toggle="modal" data-bs-target="#singleModal">
          <font-awesome-icon icon="times" /> Cancel
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <form @submit.prevent="makePurchase">
  
        <!-- Purchase Details -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="truck" />
            </div>
            <div>
              <p class="section-title">Purchase Details</p>
              <p class="section-sub">Set date, store and supplier</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Date & Time <span class="req">*</span></label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker v-model="date" :enableTimePicker="true" class="clean-datepicker" />
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Store <span class="req">*</span></label>
              <Multiselect
                v-model="store_id" label="name" :options="getStore"
                placeholder="Select store" :searchable="true"
                :create-option="false" :close-on-select="true"
                class="custom-multiselect"
              />
            </div>
            <div class="field-group">
              <label class="field-label">Supplier <span class="req">*</span></label>
              <Multiselect
                v-model="supplier" label="name" :options="supplier_arr"
                placeholder="Select or create supplier" :searchable="true"
                :create-option="true" :close-on-select="true"
                class="custom-multiselect"
              />
              <span class="field-hint">Search or type to create a new supplier</span>
            </div>
          </div>
        </div>
  
        <!-- Products -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="boxes" />
            </div>
            <div>
              <p class="section-title">Products</p>
              <p class="section-sub">Add purchased products and quantities</p>
            </div>
            <button type="button" class="btn-add-product-modal ms-auto" @click="modalToggle">
              <font-awesome-icon icon="plus" class="me-1" /> New Product
            </button>
          </div>
  
          <div class="table-scroll-wrap">
            <table class="adj-table">
              <thead>
                <tr>
                  <th style="min-width:200px">Product</th>
                  <th style="min-width:90px">Avail. Qty</th>
                  <th style="min-width:150px">Variant / UOM</th>
                  <th style="min-width:110px">Cost Price</th>
                  <th style="min-width:80px">Qty</th>
                  <th style="min-width:150px">Best Before</th>
                  <th style="min-width:110px">Amount</th>
                  <th style="min-width:44px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, index) in product" :key="index">
  
                  <td>
                    <Multiselect
                      v-model="p.product_id" label="name" :options="getProducts"
                      :canClear="false" :searchable="true"
                      :create-option="false" :close-on-select="true"
                      @select="handleChange(p.product_id, index)"
                      placeholder="Select product"
                      class="custom-multiselect custom-multiselect--sm"
                    >
                      <template #option="{ option }">
                        <span v-html="option.name"></span>
                      </template>
                      <template #singlelabel="{ value }">
                        <span class="multiselect-single-label" v-html="value.name"></span>
                      </template>
                    </Multiselect>
                  </td>
                  <td>
                    <input disabled type="text" class="tbl-input tbl-input--center" v-model="p.available_qty" />
                  </td>
                  <td>
                    <select v-if="p.variant.length > 0" class="tbl-input tbl-select"
                      v-model="p.product_variant_id"
                      @change="handleVariant(p, index, p.product_variant_id)">
                      <option disabled value="">Select variant</option>
                      <option v-for="variant in p.variant" :key="variant.id" :value="variant.id">{{ variant.variant_name }}</option>
                    </select>
                    <input v-else type="text" class="tbl-input" :value="p.unit_of_measurement" disabled />
                  </td>
                  <td>
                    <input type="number" class="tbl-input" min="0" step="any"
                      v-model="p.cost_price" :disabled="p.disable_field"
                      @keyup="calculateAmount(p)" />
                  </td>
                  <td>
                    <input type="number" class="tbl-input tbl-input--center" min="1"
                      v-model="p.quantity" :disabled="p.disable_field"
                      @keyup="calculateAmount(p)"
                      onkeypress="return /^[0-9]+$/i.test(event.key)" />
                  </td>
                  <td>
                    <div class="datepicker-wrap">
                      <Datepicker
                        :disabled="p.disable_field"
                        v-model="p.best_before"
                        :enableTimePicker="false"
                        class="clean-datepicker clean-datepicker--sm"
                      />
                    </div>
                  </td>
                  <td>
                    <input type="number" class="tbl-input tbl-input--amount" min="0" step="any"
                      v-model="p.amount" :disabled="p.disable_field"
                      @keyup="calculateTotal()" />
                  </td>
                  <td>
                    <button class="btn-remove-row" type="button"
                      @click.prevent="removeProduct(index, calculateTotal)"
                      title="Remove row">
                      <font-awesome-icon icon="times" />
                    </button>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer-actions">
            <button class="btn-add-row" type="button" @click="addProduct">
              <font-awesome-icon icon="plus" class="me-2" /> Add Row
            </button>
  
            <!-- Total -->
            <div class="total-chip">
              <span class="total-chip-label">Total Amount</span>
              <span class="total-chip-value">
                {{ $filters.formatMoney(form.total_cost, currentUserCurrency?.symbol) }}
              </span>
            </div>
          </div>
        </div>
  
        <!-- Payment -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--green">
              <font-awesome-icon icon="credit-card" />
            </div>
            <div>
              <p class="section-title">Payment</p>
              <p class="section-sub">Choose payment method and enter amounts</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Payment Type</label>
              <select v-model="form.payment_type" class="field-input field-select" @change="handlePaymentType()">
                <option :value="pay.value" v-for="pay in paymentTypes" :key="pay.id">{{ pay.name }}</option>
              </select>
              <span class="field-error" v-if="split_payment_error">{{ split_payment_error }}</span>
            </div>
  
            <div class="field-group"
              v-show="form.payment_type == 'cash' || form.payment_type == 'card' || form.payment_type == 'part'">
              <label class="field-label">Paid Amount</label>
              <input type="number" min="0" step="any" class="field-input"
                v-model="form.paid_amount" :disabled="paid_disabled" @keyup="handlePaidInput" />
              <span class="field-error" v-if="paid_amount_error">{{ paid_amount_error }}</span>
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'cash' || form.payment_type == 'card'">
              <label class="field-label">Change</label>
              <input type="number" min="0" step="any" class="field-input field-input--readonly" disabled v-model="form.change" />
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'credit' || form.payment_type == 'part'">
              <label class="field-label">Balance</label>
              <input type="text" class="field-input field-input--readonly" v-model="form.balance" disabled />
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'split'">
              <label class="field-label">Cash</label>
              <input type="number" class="field-input" name="cash"
                v-model="form.cash" :disabled="cash_disabled" @keyup="handleSplitpayment($event)" />
              <span class="field-error" v-if="split_cash_error">{{ split_cash_error }}</span>
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'split'">
              <label class="field-label">Card</label>
              <input type="number" min="0" step="any" class="field-input" name="card"
                v-model="form.card" :disabled="card_disabled" @keyup="handleSplitpayment($event)" />
              <span class="field-error" v-if="split_card_error">{{ split_card_error }}</span>
            </div>
          </div>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" class="btn-outline" data-bs-toggle="modal" data-bs-target="#singleModal">
            Cancel
          </button>
          <button type="submit" class="btn-primary-action" :disabled="crudLoading">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ crudLoading ? 'Processing...' : 'Complete Purchase' }}
          </button>
        </div>
  
      </form>
  
      <!-- Cancel Confirm Modal -->
      <Alert
        :modalId="'singleModal'"
        @alertModal="handleAction()"
        :modalText="'Are you sure you want to cancel this purchase?'"
        :action="'alertModal'"
        title="Cancel Purchase"
        confirm-label="Yes, Cancel"
        confirm-class="modal-btn--danger"
        icon="times-circle"
      />
  
      <!-- Create Product Modal -->
      <div
        class="modal fade"
        id="create-product"
        :class="{ show: modal_active, 'd-block': modal_active }"
        tabindex="-1" aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-scrollable modal-lg modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(13,148,136,.1);color:#0d9488;">
                <font-awesome-icon icon="box" />
              </div>
              <div>
                <h6 class="modal-clean-title">Add New Product</h6>
                <p class="modal-clean-sub">Create a product to add to your inventory</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="modalToggle" aria-label="Close"></button>
            </div>
            <div class="modal-body pt-0">
              <createProductForm @saveProduct="callProductFunc" @modalToggle="modalToggle" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="modal_active" class="modal-backdrop fade show"></div>
  
    </div>
  </template>
  
  <script>
  import { ref } from '@vue/reactivity';
  import { onMounted } from '@vue/runtime-core';
  import axios from 'axios';
  import { useReusable } from '../../../lib/reusable';
  import Multiselect from '@vueform/multiselect';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Alert from '../../../components/AlertModal.vue';
  import createProductForm from '../inventorymgt/component/createProductForm';
  
  const EMPTY_PRODUCT = () => ({
    product_id: '', available_qty: 0,
    cost_price: '', new_cost_price: '', selling_price: '',
    variant: [], product_variant_id: '',
    quantity: 1, amount: (0).toFixed(2),
    best_before: null, disable_field: true,
  });
  
  export default {
    name: 'MakePurchase',
    components: { Multiselect, Alert, createProductForm },
    mixins: [reusableMixin],
  
    setup() {
      const { paymentTypes, allPaymentTypes, getStore, allStore } = useReusable();
  
      const toast            = useToast();
      const router           = useRouter();
      const validationErrors = ref([]);
      const store_id         = ref(null);
      const supplier_arr     = ref([]);
      const supplier         = ref(null);
      const getProducts      = ref([]);
      const currentVariant   = ref([]);
      const product          = ref(Array.from({ length: 5 }, EMPTY_PRODUCT));
      const date             = ref(new Date());
      const crudLoading      = ref(false);
      const product_result   = ref([]);
      const modal_active     = ref(false);
      const is_product_in_store_error = ref('');
      const paid_disabled    = ref(true);
      const cash_disabled    = ref(true);
      const card_disabled    = ref(true);
      const paid_amount_error   = ref('');
      const split_payment_error = ref('');
      const split_cash_error    = ref('');
      const split_card_error    = ref('');
  
      const form = ref({
        payment_type: 'cash', total_cost: '0.00',
        paid_amount: null, change: null, balance: null,
        cash: (0).toFixed(2), card: (0).toFixed(2),
      });
  
      const { getProductInStore, removeProduct } = useReusable(product);
  
      const modalToggle = () => { modal_active.value = !modal_active.value; };
      const handleAction = () => { router.go(-1); };
  
      const handleSplitpayment = (event) => {
        const name = event.target.name;
        const cash = form.value.cash || 0;
        const card = form.value.card || 0;
        split_cash_error.value = Math.sign(cash) === -1 ? 'Cash must not be less than zero' : '';
        split_card_error.value = Math.sign(card) === -1 ? 'Card must not be less than zero' : '';
        if (name === 'cash') {
          form.value.card = parseFloat(form.value.total_cost - cash).toFixed(2);
          if (Math.sign(form.value.card) === -1) { form.value.card = (0).toFixed(2); form.value.cash = parseFloat(form.value.total_cost).toFixed(2); }
        } else {
          form.value.cash = parseFloat(form.value.total_cost - card).toFixed(2);
          if (Math.sign(form.value.cash) === -1) { form.value.cash = (0).toFixed(2); form.value.card = parseFloat(form.value.total_cost).toFixed(2); }
        }
      };
  
      const handlePaymentType = () => {
        const t = form.value.payment_type;
        if (t === 'credit') {
          Object.assign(form.value, { balance: form.value.total_cost, paid_amount: null, cash: null, card: null });
        } else if (t === 'part') {
          Object.assign(form.value, { change: null, balance: form.value.total_cost, paid_amount: (0).toFixed(2), cash: null, card: null });
        } else if (t === 'split') {
          Object.assign(form.value, { balance: null });
        } else if (t === 'cash' || t === 'card') {
          Object.assign(form.value, { cash: null, card: null, balance: null, paid_amount: form.value.total_cost, change: (0).toFixed(2) });
        }
      };
  
      const calculateAmount = (p) => {
        p.amount = (parseFloat(p.cost_price) * parseFloat(p.quantity)).toFixed(2);
        if (isNaN(p.amount)) p.amount = '?';
        if (Math.sign(p.quantity) === -1) p.quantity = Math.abs(p.quantity);
        if (p.quantity > 0) calculateTotal();
      };
  
      const calculateTotal = () => {
        const total = product.value.reduce((a, b) => { const v = parseFloat(b.amount); return a + (isNaN(v) ? 0 : v); }, 0);
        form.value.total_cost = total.toFixed(2);
        form.value.paid_amount = form.value.total_cost;
        const hasAmount = form.value.paid_amount !== '?';
        paid_disabled.value = !hasAmount; cash_disabled.value = !hasAmount; card_disabled.value = !hasAmount;
        if (form.value.payment_type === 'cash' || form.value.payment_type === 'card')
          form.value.change = (form.value.paid_amount - total).toFixed(2);
        form.value.balance = form.value.total_cost;
      };
  
      const handlePaidInput = () => {
        if (form.value.payment_type === 'part') {
          if (form.value.paid_amount > form.value.total_cost) {
            form.value.paid_amount = parseFloat(form.value.total_cost).toFixed(2);
            form.value.balance     = '0.00';
            paid_amount_error.value = 'Paid amount must not exceed total';
            setTimeout(() => { paid_amount_error.value = ''; }, 2000);
          } else {
            form.value.balance = (form.value.total_cost - form.value.paid_amount).toFixed(2);
          }
        } else if (form.value.payment_type === 'cash' || form.value.payment_type === 'card') {
          form.value.change = form.value.paid_amount > form.value.total_cost
            ? (form.value.paid_amount - form.value.total_cost).toFixed(2) : null;
        }
      };
  
      const addProduct = () => product.value.push({ ...EMPTY_PRODUCT() });
  
      const callProductFunc = async () => { await allProducts(); };
  
      const allProducts = async () => {
        const result = await axios.get('inventory/product/active/all');
        product_result.value = result.data.data;
        getProducts.value = [
          { value: 0, name: "<span style='color:grey;'>--- Products ---</span>" },
          ...result.data.data.map(p => ({
            value: p.id,
            name: p.bar_code ? `${p.product_name} (${p.bar_code})` : p.product_name,
          })),
        ];
      };
  
      const allSuppliers = async () => {
        const res = await axios.get('/settings/suppliers/all');
        supplier_arr.value = res?.data?.data?.map(s => ({ value: s.id, name: s.supplier_name })) ?? [];
      };
  
      const handleChange = async (id, index) => {
        const res            = await axios.get(`/inventory/product/${id}?store_id=${store_id.value}`);
        const single_product = res?.data?.data;
        const variant        = single_product.variants;
        let cost             = single_product?.cost_price;
        let available_qty    = single_product?.store_qty ?? single_product?.quantity ?? 0;

        if (single_product?.store_product != null) {
          cost = single_product?.store_product?.cost_price;
        }
  
        const occurrences = product.value.filter(v => v.product_id == single_product.id);
        if (occurrences.length > 1) {
          Object.assign(product.value[index], EMPTY_PRODUCT());
          toast.warning('Product already selected');
          calculateTotal(); return;
        }
  
        if (product.value.filter(v => v.product_id === '').length === 0) addProduct();
  
        Object.assign(product.value[index], {
          selling_price:       parseFloat(single_product.selling_price).toFixed(2),
          cost_price:          parseFloat(cost).toFixed(2),
          new_cost_price:      cost,
          available_qty,
          best_before:         null,
          total_price:         cost * product.value[index].quantity,
          amount:              parseFloat(cost * product.value[index].quantity).toFixed(2),
          variant,
          disable_field:       false,
          unit_of_measurement: single_product?.unit_of_measurement,
          product_variant_id:  '',
        });
  
        calculateTotal();
      };
  
      const makePurchase = async () => {
        crudLoading.value = true;
        const valid_products  = product.value.filter(p => p.product_id !== '');
        if (form.value.payment_type === 'split') {
          const sum = parseFloat(form.value.cash) + parseFloat(form.value.card);
          if (sum !== parseFloat(form.value.total_cost)) {
            crudLoading.value = false;
            toast.error('Cash and card values must equal the total'); return;
          }
        }
        axios.post('/inventory/purchase', {
          others:        form.value,
          total_amount:  form.value.total_cost,
          store_id:      store_id.value,
          time:          date.value.toTimeString().slice(0, 8),
          date:          date.value.toISOString().split('T')[0],
          supplier_id:   supplier.value,
          purchase_item: valid_products,
        }).then(res => {
          crudLoading.value = false;
          router.push({ name: 'All Purchase' });
          toast.success(res.data.message);
        }).catch(error => {
          crudLoading.value = false;
          const msg = error.response?.data?.message;
          const toArray = v => Array.isArray(v) ? v : (v ? [v] : []);
          if (error.response?.status == 422) { toast.warning('There are errors in your input'); validationErrors.value = toArray(msg); }
          else if (error.response?.status == 400) { validationErrors.value = toArray(msg); toast.error(Array.isArray(msg) ? 'Bad request, please check your input' : msg); }
          else if (error.response?.status == 500) toast.error('An error occurred, please try again');
        });
      };
  
      const handleVariant = (p, index, vId) => {
        if (p.variant.length > 0) {
          const variant = p.variant.find(({ id }) => id == vId);
          currentVariant.value = variant;
          p.cost_price         = (p.new_cost_price * parseFloat(variant.variant_quantity)).toFixed(2);
          product.value[index].amount = (p.cost_price * product.value[index].quantity).toFixed(2);
        }
        calculateTotal();
      };
  
      onMounted(() => { allSuppliers(); allProducts(); allStore(); allPaymentTypes(); });
  
      return {
        modal_active, modalToggle, callProductFunc, product_result, handleAction,
        allPaymentTypes, paymentTypes, getProductInStore, removeProduct,
        getStore, handleChange, product, store_id, getProducts,
        supplier_arr, supplier, allSuppliers, addProduct, calculateTotal, form, date,
        calculateAmount, paid_disabled, cash_disabled, card_disabled,
        paid_amount_error, split_payment_error, is_product_in_store_error,
        split_cash_error, split_card_error, validationErrors,
        handlePaidInput, handlePaymentType, handleSplitpayment,
        makePurchase, handleVariant, crudLoading,
      };
    },
  };
  </script>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <style scoped>
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue  { background:rgba(0,74,173,.1);   color:#004AAD; }
  .section-icon--teal  { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-icon--green { background:rgba(22,163,74,.1);  color:#16a34a; }
  .section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  .fields-grid   { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .field-group   { display:flex; flex-direction:column; gap:6px; }
  .field-label   { font-size:13px; font-weight:600; color:#1a2b45; }
  .req           { color:#e03535; }
  .field-hint    { font-size:11.5px; color:#7a90b0; }
  .field-error   { font-size:11.5px; color:#e03535; }
  
  .field-input { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus    { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input:disabled { background:#f8fafc; color:#aab8cc; cursor:not-allowed; }
  .field-input--readonly { background:#f8fafc; }
  .field-select { appearance:auto; cursor:pointer; }
  
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  :deep(.clean-datepicker.is-active) { border-color:#004AAD !important; box-shadow:0 0 0 3px rgba(0,74,173,.08) !important; }
  :deep(.clean-datepicker--sm) { padding-left:8px !important; }
  :deep(.clean-datepicker--sm input) { font-size:12.5px; }
  
  :deep(.custom-multiselect) { --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-font-size:13.5px; --ms-ring-color:rgba(0,74,173,.08); }
  :deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
  :deep(.custom-multiselect--sm)       { --ms-py:6px; --ms-font-size:13px; }
  
  .btn-add-product-modal { display:inline-flex; align-items:center; background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; font-size:12.5px; font-weight:600; padding:7px 14px; border-radius:8px; cursor:pointer; transition:background .15s; flex-shrink:0; }
  .btn-add-product-modal:hover { background:#dcfce7; }
  
  .table-scroll-wrap { overflow-x:auto; }
  .adj-table   { width:100%; border-collapse:collapse; }
  .adj-table thead tr { background:#f8fafc; }
  .adj-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 8px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .adj-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .adj-table tbody tr:last-child { border-bottom:none; }
  .adj-table tbody tr:hover { background:#fafbfc; }
  .adj-table tbody td { padding:8px; vertical-align:top; }
  
  .tbl-input { width:100%; padding:7px 9px; border:1px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s; }
  .tbl-input:focus    { border-color:#004AAD; box-shadow:0 0 0 2px rgba(0,74,173,.08); }
  .tbl-input:disabled { background:#f8fafc; color:#aab8cc; }
  .tbl-select  { appearance:auto; cursor:pointer; }
  .tbl-input--center { text-align:center; }
  .tbl-input--amount { font-weight:700; color:#004AAD; }
  
  .btn-remove-row { width:30px; height:30px; border-radius:7px; background:#fff0f0; border:1px solid #fac5c5; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; transition:background .15s; }
  .btn-remove-row:hover { background:#ffe0e0; }
  
  .table-footer-actions { display:flex; align-items:center; justify-content:space-between; margin-top:14px; flex-wrap:wrap; gap:12px; }
  .btn-add-row { display:inline-flex; align-items:center; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-add-row:hover { background:#d6e4fa; }
  
  .total-chip { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 18px; }
  .total-chip-label { font-size:13px; font-weight:600; color:#7a90b0; }
  .total-chip-value { font-size:20px; font-weight:800; color:#004AAD; }
  
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; padding-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 28px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  .modal-clean { border-radius:14px; border:none; box-shadow:0 20px 60px rgba(0,0,0,.12); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
  input[type=number] { -moz-appearance:textfield; }
  
  @media(max-width:991px) { .fields-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px) {
    .fields-grid { grid-template-columns:1fr; }
    .form-card   { padding:16px; }
    .form-footer { flex-direction:column; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
    .table-footer-actions { flex-direction:column; align-items:stretch; }
    .total-chip { justify-content:center; }
  }
  </style>